<template>
  <div class="iq-card mb-0" style="min-width: 300px">
    <div class="iq-card-header d-flex justify-content-between">
      <div class="iq-header-title">
        <h4 class="card-title">
          {{ data.massId ? 'Edit Asset' : 'Add New Asset' }} for {{ activeCompany.compName }}
        </h4>
      </div>
    </div>
    <div class="iq-card-body">

      <!-- Image -->
      <div class="row">
        <div class="form-group col-md-6">
          <label class="d-block form-label text-left">Image:</label>
          <b-form-file
            class="mb-2"
            placeholder="Choose a file or drag and drop it here..."
            drop-placeholder="Drop file here..."
            accept=".jpeg,.jpg,.png"
            @change="previewImage"
          />
          <img
            v-if="imagePreview"
            :src="imagePreview"
            alt="Selected Image"
            class="img-thumbnail mt-2"
            style="max-width: 100%; height: auto;"
          />
        </div>
      </div>

      <!-- Basic Info -->
      <p class="text-uppercase text-secondary small font-weight-bold border-bottom pb-1 mb-3">
        Basic Info
      </p>
      <div class="row">
        <div class="form-group col-md-6">
          <label class="d-block form-label text-left">Name:</label>
          <input
            v-model="data.massName"
            type="text"
            class="form-control"
            :class="errors.massName ? 'is-invalid' : ''"
            placeholder="Asset name"
            :disabled="isSubmit"
            autocomplete="off"
          />
        </div>

        <div class="form-group col-md-6">
          <label class="d-block form-label text-left">Asset Type:</label>
          <b-form-select
            v-model="data.massMatyId"
            class="form-control"
            :class="errors.massMatyId ? 'is-invalid' : ''"
            :options="assetTypes"
            :disabled="isSubmit"
          />
        </div>

        <div class="form-group col-md-6">
          <label class="d-block form-label text-left">System Type:</label>
          <b-form-select
            v-model="data.massSystemType"
            class="form-control"
            :class="errors.massSystemType ? 'is-invalid' : ''"
            :options="['FMS', 'EMS', 'BM']"
            :disabled="isSubmit"
          />
        </div>

        <div class="form-group col-md-6">
          <label class="d-block form-label text-left">Installation Date:</label>
          <DatePicker
            :value="data.massInstallationDate ? new Date(data.massInstallationDate) : null"
            mode="date"
            :masks="{ input: 'DD MMM YYYY' }"
            @input="(date) => onDateSelect('massInstallationDate', date)"
            class="w-100"
          >
            <template v-slot="{ inputValue, inputEvents }">
              <div class="input-group">
                <input
                  class="form-control"
                  :class="errors.massInstallationDate ? 'is-invalid' : ''"
                  :value="inputValue"
                  v-on="inputEvents"
                  placeholder="Pick a date..."
                  :disabled="isSubmit"
                  autocomplete="off"
                />
                <div class="input-group-append">
                  <span class="input-group-text" style="cursor:pointer;">
                    <i class="ri-calendar-line"></i>
                  </span>
                </div>
              </div>
            </template>
          </DatePicker>
        </div>

        <div class="form-group col-md-6">
          <label class="d-block form-label text-left">Subscription Expired At:</label>
          <DatePicker
            :value="data.massSubscriptionExpiredAt ? new Date(data.massSubscriptionExpiredAt.replace(' ', 'T')) : null"
            mode="date"
            :masks="{ input: 'DD MMM YYYY' }"
            @input="(date) => onDateSelect('massSubscriptionExpiredAt', date, true)"
            class="w-100"
          >
            <template v-slot="{ inputValue, inputEvents }">
              <div class="input-group">
                <input
                  class="form-control"
                  :class="errors.massSubscriptionExpiredAt ? 'is-invalid' : ''"
                  :value="inputValue"
                  v-on="inputEvents"
                  placeholder="Pick date & time..."
                  :disabled="isSubmit"
                  autocomplete="off"
                />
                <div class="input-group-append">
                  <span class="input-group-text" style="cursor:pointer;">
                    <i class="ri-calendar-line"></i>
                  </span>
                </div>
              </div>
            </template>
          </DatePicker>
        </div>
      </div>

      <!-- Contact Person (PIC) -->
      <p class="text-uppercase text-secondary small font-weight-bold border-bottom pb-1 mb-3 mt-2">
        Contact Person (PIC)
      </p>
      <div class="row">
        <div class="form-group col-md-6">
          <label class="d-block form-label text-left">PIC Name:</label>
          <input
            v-model="data.massPicName"
            type="text"
            class="form-control"
            :class="errors.massPicName ? 'is-invalid' : ''"
            placeholder="PIC name"
            :disabled="isSubmit"
            autocomplete="off"
          />
        </div>

        <div class="form-group col-md-6">
          <label class="d-block form-label text-left">PIC Contact:</label>
          <input
            v-model="data.massPicContact"
            type="text"
            class="form-control"
            :class="errors.massPicContact ? 'is-invalid' : ''"
            placeholder="Phone number"
            :disabled="isSubmit"
            autocomplete="off"
          />
        </div>

        <div class="form-group col-md-6">
          <label class="d-block form-label text-left">PIC Email:</label>
          <input
            v-model="data.massPicEmail"
            type="text"
            class="form-control"
            :class="errors.massPicEmail ? 'is-invalid' : ''"
            placeholder="email@example.com"
            :disabled="isSubmit"
            autocomplete="off"
          />
        </div>

        <div class="form-group col-md-6">
          <label class="d-block form-label text-left">PIC Email CC:</label>
          <input
            v-model="data.massPicEmailCc"
            type="text"
            class="form-control"
            :class="errors.massPicEmailCc ? 'is-invalid' : ''"
            placeholder="cc@example.com"
            :disabled="isSubmit"
            autocomplete="off"
          />
        </div>
      </div>

      <!-- Connectivity -->
      <p class="text-uppercase text-secondary small font-weight-bold border-bottom pb-1 mb-3 mt-2">
        Connectivity & Hardware
      </p>
      <div class="row">
        <div class="form-group col-md-6">
          <label class="d-block form-label text-left">GSM No:</label>
          <input
            v-model="data.massGsmNo"
            type="text"
            class="form-control"
            :class="errors.massGsmNo ? 'is-invalid' : ''"
            placeholder="GSM number"
            :disabled="isSubmit"
            autocomplete="off"
          />
        </div>

        <div class="form-group col-md-6">
          <label class="d-block form-label text-left">IMEI:</label>
          <input
            v-model="data.massImei"
            type="text"
            class="form-control"
            :class="errors.massImei ? 'is-invalid' : ''"
            placeholder="IMEI number"
            :disabled="isSubmit"
            autocomplete="off"
          />
        </div>

        <div class="form-group col-md-6">
          <label class="d-block form-label text-left">Telco Operator:</label>
          <input
            v-model="data.massTelcoOperator"
            type="text"
            class="form-control"
            :class="errors.massTelcoOperator ? 'is-invalid' : ''"
            placeholder="Telco operator"
            :disabled="isSubmit"
            autocomplete="off"
          />
        </div>

        <div class="form-group col-md-6">
          <label class="d-block form-label text-left">Satellite ID:</label>
          <input
            v-model="data.massSateliteId"
            type="text"
            class="form-control"
            :class="errors.massSateliteId ? 'is-invalid' : ''"
            placeholder="Satellite ID"
            :disabled="isSubmit"
            autocomplete="off"
          />
        </div>

        <div class="form-group col-md-6">
          <label class="d-block form-label text-left">Satellite Operator:</label>
          <input
            v-model="data.massSateliteOperator"
            type="text"
            class="form-control"
            :class="errors.massSateliteOperator ? 'is-invalid' : ''"
            placeholder="Satellite operator"
            :disabled="isSubmit"
            autocomplete="off"
          />
        </div>
      </div>

      <!-- Actions -->
      <div class="form-group mt-2">
        <b-button @click="cancel" class="mr-1" size="sm" variant="outline-danger" :disabled="isSubmit">
          <div class="mx-2">Cancel</div>
        </b-button>
        <b-button class="ml-1" size="sm" type="submit" variant="primary" @click="handleSubmit" :disabled="isSubmit">
          <div class="mx-2">
            <i v-if="isSubmit" class="ri-loader-line"></i>
            Submit
          </div>
        </b-button>
      </div>

    </div>
  </div>
</template>

<script>
import AssetSvc from '@/services/AssetSvc';
import { DatePicker } from 'v-calendar';

export default {
  name: 'AssetForm',
  components: {
    DatePicker,
  },
  computed: {
    activeCompany() {
      return this.$store.getters['auth/activeCompany'];
    },
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
    assetTypes: {
      required: true,
    },
  },
  data() {
    return {
      errors: {},
      isSubmit: false,
      imagePreview: null,
      imageFile: null,
    };
  },
  methods: {
    async handleSubmit() {
      try {
        this.isSubmit = true;
        const compId = this.activeCompany.compId;
        const massId = this.data.massId || undefined;
        const formData = { ...this.data, massCompId: compId, massDataId: compId };

        if (massId) {
          delete formData.massDevice;
          delete formData.massImage;
        }

        const assetResponse = await AssetSvc.addOrUpdateAsset(massId, formData);
        if (assetResponse.data.status.error === 0) {
          const assetId = massId || assetResponse.data.id;

          if (this.imageFile) {
            const uploadResponse = await AssetSvc.uploadAssetImages(assetId, this.imageFile);
            console.debug('uploadResponse : ', uploadResponse);
          }

          this.$toast.success(massId ? 'Asset updated' : 'New asset created');
          this.$emit('onSuccess');
        } else if (assetResponse.data.status.errorCode === 401) {
          this.errors = assetResponse.data.status.message;
          this.$toast.error('Invalid data');
        }
      } catch (e) {
        this.$toast.error(e.response?.data?.status?.message || 'An error occurred');
      } finally {
        this.isSubmit = false;
      }
    },
    cancel() {
      this.$emit('onCancel');
    },
    onDateSelect(fieldName, date, withTime = false) {
      if (date) {
        const d = new Date(date);
        const pad = (n) => String(n).padStart(2, '0');
        const dateStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        this.data[fieldName] = withTime ? `${dateStr} 00:00:00` : dateStr;
      } else {
        this.data[fieldName] = null;
      }
    },
    onDatetimeSelect(fieldName, date) {
      if (date) {
        const d = new Date(date);
        const pad = (n) => String(n).padStart(2, '0');
        this.data[fieldName] = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      } else {
        this.data[fieldName] = null;
      }
    },
    previewImage(event) {
      const file = event.target.files[0];
      if (file) {
        this.imagePreview = URL.createObjectURL(file);
        this.imageFile = file;
      } else {
        this.imagePreview = null;
        this.imageFile = null;
      }
    },
  },
};
</script>

<style scoped>
.vs__dropdown-toggle {
  border: 0;
}

.vs__selected {
  color: #8c91b6;
}
</style>
